/**
 * Free-flow shared chat input (task 20.3, R31.2).
 *
 * Free-flow mode is a single shared conversation channel any participant can
 * post into at any time. This is that message box: type a line, submit, and it
 * posts to the campaign chat (which the server appends to the log and fans back
 * out over the WebSocket free-flow push). There is NO optimistic local echo:
 * the server's free-flow push (keyed by the authoritative conversation-log seq)
 * is the single source folded into the journal, so the line renders exactly
 * once when the push arrives.
 */

import { useRef, useState, useLayoutEffect } from "react";
import type { MessageMode } from "@rufus/shared-types";

// Auto-grow bounds for the composer textarea (in rows). It starts at MIN so the
// idle box is roomy enough for a sentence or two, and grows with the content up
// to MAX, after which it scrolls — so a long backstory never pushes the Send
// button off-screen.
const MIN_ROWS = 3;
const MAX_ROWS = 10;

export interface FreeFlowChatProps {
  /**
   * Post the message; resolves when the server accepts it. `mode` is the OOC/IC
   * axis the composer toggle selects — `ic` for an in-character action/speech,
   * `ooc` for an out-of-character question to the DM.
   */
  readonly onSend: (text: string, mode: MessageMode) => Promise<void> | void;
  readonly disabled?: boolean;
  /**
   * OPTIONAL controlled draft. ALL-OR-NOTHING with {@link onChange}: supply BOTH
   * to make the composer controlled by the parent (so e.g. the play screen can
   * APPEND a roll tag like `[d20 roll of 5]` into the box the player is typing
   * in), or NEITHER to let the composer own its draft (the standalone default).
   * Passing only one is a mis-wire — the lone prop is ignored and dev gets a
   * `console.warn`. On submit the composer clears via whichever setter is
   * active, so a controlled parent sees the draft go back to "".
   */
  readonly value?: string;
  readonly onChange?: (text: string) => void;
}

export function FreeFlowChat({ onSend, disabled, value, onChange }: FreeFlowChatProps) {
  // Controlled only when the parent supplies BOTH the value and its setter;
  // otherwise fall back to internal state so standalone usage is unchanged. The
  // pair is ALL-OR-NOTHING: passing `value` WITHOUT `onChange` (or vice-versa)
  // silently uses internal state and IGNORES the lone prop. React does not warn
  // on that (we are not driving a DOM `value`/`onChange` directly), so warn
  // ourselves — a half-wired controlled composer is a mis-wire, never
  // intentional, so the lone-prop case only fires on a genuine developer error.
  const controlled = value !== undefined && onChange !== undefined;
  if ((value !== undefined) !== (onChange !== undefined)) {
    console.warn(
      "FreeFlowChat: `value` and `onChange` are all-or-nothing — pass BOTH to make the " +
        "composer controlled, or NEITHER to use internal state. The lone prop is ignored.",
    );
  }
  const [internalText, setInternalText] = useState("");
  const text = controlled ? value : internalText;
  const setText = controlled ? onChange : setInternalText;
  const [mode, setMode] = useState<MessageMode>("ic");
  const [busy, setBusy] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // AUTO-GROW: after each render, reset the height and set it to fit the content,
  // clamped to [MIN_ROWS, MAX_ROWS]. useLayoutEffect so the resize happens before
  // paint (no flicker). lineHeight is read from the computed style so this tracks
  // whatever font sizing the browser applies.
  useLayoutEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    const cs = window.getComputedStyle(ta);
    const lineHeight = parseFloat(cs.lineHeight) || 20;
    const padding = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) || 0;
    const minH = lineHeight * MIN_ROWS + padding;
    const maxH = lineHeight * MAX_ROWS + padding;
    ta.style.height = "auto"; // reset so scrollHeight reflects the true content
    ta.style.height = `${Math.min(Math.max(ta.scrollHeight, minH), maxH)}px`;
    ta.style.overflowY = ta.scrollHeight > maxH ? "auto" : "hidden";
  }, [text]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await onSend(trimmed, mode);
      setText("");
    } finally {
      setBusy(false);
    }
  };

  // Enter sends; Shift+Enter inserts a newline (the chat convention). We only
  // intercept a bare Enter — any modifier (Shift for a deliberate newline, or
  // Ctrl/Cmd/Alt) falls through to the textarea's default so multi-paragraph
  // input stays possible. Guarded against IME composition so Enter that is
  // committing a composed character (CJK input) does not submit mid-word.
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit(e);
    }
  };

  const ooc = mode === "ooc";
  return (
    <form className="free-flow-chat" onSubmit={submit} aria-label="Free-flow chat">
      {/*
        The in-character / out-of-character toggle. In character (default) is a
        line your character says or does and the DM adjudicates; out of
        character is a meta/informational question to the DM ("what do I see?",
        "repeat the letter") that is answered plainly without advancing the
        scene. A single checkbox keeps the choice visible and one tap away.
      */}
      <label className="chat-mode-toggle">
        <input
          type="checkbox"
          checked={ooc}
          onChange={(e) => setMode(e.target.checked ? "ooc" : "ic")}
          disabled={disabled || busy}
          data-testid="chat-mode-ooc"
        />
        Out of character (ask the DM)
      </label>
      <label htmlFor="chat-input">
        {ooc ? "Ask the DM" : "Say something"}
      </label>
      <textarea
        id="chat-input"
        ref={taRef}
        rows={MIN_ROWS}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={
          ooc
            ? "Ask a question about the game…"
            : "Post to the shared channel…"
        }
        disabled={disabled || busy}
        aria-describedby="chat-input-hint"
        style={{
          display: "block",
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          resize: "vertical",
          fontFamily: "inherit",
          fontSize: "inherit",
          lineHeight: 1.4,
          padding: "0.5rem",
        }}
      />
      <p id="chat-input-hint" className="chat-input-hint" style={{ fontSize: "0.85em", opacity: 0.7, margin: "0.25rem 0" }}>
        Press <kbd>Enter</kbd> to send, <kbd>Shift</kbd>+<kbd>Enter</kbd> for a new line.
      </p>
      <button type="submit" disabled={disabled || busy || text.trim() === ""}>
        {ooc ? "Ask" : "Send"}
      </button>
    </form>
  );
}
